import { AssetItem } from '@design-system/asset-item';
import { PageCard } from '@design-system/page-card';
import { PictoItem } from '@design-system/picto-item';
import { ProductCard } from '@design-system/product-card';
import React, { ReactNode } from 'react';

import {
  ItemListingCarouselProps,
  ItemListingProps,
} from '~/components/ItemListing/models';
import { assetItemMapper } from '~/mappers/assetItem';
import { AssetItem as AssetItemType } from '~/mappers/assetItem/models';
import { buttonMapper } from '~/mappers/button';
import {
  pageCardRefMapper,
  productCardCategoryMapper,
  productCardRefMapper,
  relatedPageCardMapper,
} from '~/mappers/card';
import {
  PageCardRef,
  ProductCardCategory,
  ProductCardRef,
  RelatedPageCard,
  RelatedPageCardBase,
} from '~/mappers/card/models';
import { carouselMapper } from '~/mappers/carousel';
import { ctaBlockMapper } from '~/mappers/ctaBlock';
import { fluidGridMapper } from '~/mappers/fluidGrid';
import { pageBlockMapper } from '~/mappers/pageBlock';
import { pictoItemMapper } from '~/mappers/pictoItem';
import { PictoItem as PictoItemType } from '~/mappers/pictoItem/models';
import { gtmService } from '~/services/gtm';
import { SalsifyProductData } from '~/types/common';

import {
  FilterMapperResult,
  FilterSettings,
  ItemListing,
  ItemListingCarousel,
  ItemListingPageCardRef,
  ItemListingProductCardCategory,
  ItemListingProductCardRef,
  ItemListingRelatedPageCard,
  ItemListingTypes,
  ItemTypes,
  Pagination,
} from './models';

const checkAsRelatedPage = (item: ItemTypes, currentPageUid: string): boolean =>
  (item as RelatedPageCardBase).relatedPageUid !== currentPageUid;

const checkAsProductRef = (
  item: ItemTypes,
  currentPageUid: string
): boolean => {
  const {
    uid,
    fields: { fullPath, productData },
  } = item as ProductCardRef;

  return Boolean(fullPath && productData && uid !== currentPageUid);
};
const checkAsPageRef = (item: ItemTypes, currentPageUid: string): boolean => {
  const {
    uid,
    fields: { fullPath },
  } = item as ProductCardRef;

  return Boolean(fullPath && uid !== currentPageUid);
};

export const mapItems = (
  { items, typeName, ...itemsSettings }: ItemListingTypes,
  currentPageUid: string
) =>
  items
    // eslint-disable-next-line complexity
    .map((item, index) => {
      if (
        typeName.includes('asset_items') &&
        'imageSettings' in itemsSettings &&
        'horizontalView' in itemsSettings
      )
        return (
          <AssetItem
            key={(item as AssetItemType).heading.headline as string}
            {...assetItemMapper(
              item as AssetItemType,
              itemsSettings.imageSettings,
              itemsSettings.horizontalView
            )}
          />
        );

      if (typeName.includes('picto_items'))
        return (
          <PictoItem
            key={(item as PictoItemType).heading.headline as string}
            {...pictoItemMapper(item as PictoItemType)}
          />
        );

      if (typeName.includes('page_cards_by_category'))
        return checkAsRelatedPage(item, currentPageUid) ? (
          <PageCard
            key={(item as RelatedPageCard).relatedPageUid as string}
            {...relatedPageCardMapper(
              item as RelatedPageCard,
              itemsSettings as Omit<
                ItemListingRelatedPageCard,
                'type' | 'typeName'
              >
            )}
          />
        ) : null;

      if (typeName.includes('page_cards_by_ref'))
        return checkAsPageRef(item, currentPageUid) ? (
          <PageCard
            key={(item as PageCardRef).uid as string}
            {...pageCardRefMapper(
              item as PageCardRef,
              itemsSettings as Omit<ItemListingPageCardRef, 'type' | 'typeName'>
            )}
          />
        ) : null;

      if (typeName.includes('product_cards_by_category'))
        return checkAsRelatedPage(item, currentPageUid) ? (
          <ProductCard
            key={(item as ProductCardCategory).relatedPageUid as string}
            {...productCardCategoryMapper(
              item as ProductCardCategory,
              itemsSettings as Omit<
                ItemListingProductCardCategory,
                'type' | 'typeName'
              >,
              item.shopifyBlock,
              index
            )}
          />
        ) : null;

      if (typeName.includes('product_cards_by_ref'))
        return checkAsProductRef(item, currentPageUid) ? (
          <ProductCard
            key={(item as ProductCardRef).uid as string}
            {...productCardRefMapper(
              item as ProductCardRef,
              itemsSettings as Omit<
                ItemListingProductCardRef,
                'type' | 'typeName'
              >,
              index
            )}
          />
        ) : null;

      throw new Error('Not supported type');
    })
    .filter((item) => item);

export const itemListingCarouselMapper = (
  {
    componentId,
    pageBlock,
    carousel: [carousel],
    showMoreButton,
    uid,
  }: ItemListingCarousel,
  reactItems
): ItemListingCarouselProps =>
  ({
    // TODO - HKX-1311 - replace next line with: id: componentId,
    ...(componentId && { id: componentId }),
    pageBlock: pageBlockMapper(pageBlock),
    items: reactItems,
    carousel: carouselMapper(carousel, reactItems),
    ...(showMoreButton?.length && {
      showMore: ctaBlockMapper(showMoreButton.map((link) => ({ link }))),
    }),
    uid,
  } as ItemListingCarouselProps);

export const filterMapper = (
  data?: FilterSettings[]
): FilterMapperResult | undefined => {
  if (!data?.length) return undefined;

  const {
    filtersTitle,
    filterSelection: { variant, showChipPanel },
    filterDialog: { openModal, closeModal, clearFilters },
    filters,
    clearSelectedFilters,
    itemsName,
    sortBy,
    noResults: { text, typographyUI },
  } = data[0];

  return {
    filtersTitle,
    filterSelection: {
      variant,
      showChipPanel,
    },
    filterDialog: {
      openModal: buttonMapper(openModal),
      closeModal: buttonMapper(closeModal),
      clearFilters: buttonMapper(clearFilters),
    },
    filters: filters.map(({ item: { item, label } }) => ({
      options: item.map(
        ({ label: optionLabel, categoryRef: [categoryRef] }) => ({
          optionLabel: optionLabel || categoryRef.title,
          categoryRef,
        })
      ),
      label,
    })),
    clearSelectedFilters,
    itemsName,
    sortBy: {
      mainLabel: sortBy.mainLabel,
      nameAscLabel: sortBy.nameAscLabel,
      nameDescLabel: sortBy.nameDescLabel,
      recentLabel: sortBy.recentLabel,
      defaultLabel: sortBy.defaultLabel,
    },
    noResults: {
      text,
      typographyUI: {
        ...typographyUI,
      },
    },
  };
};

const paginationMapper = (pagination: Pagination) => ({
  paginationList: pagination.paginationListAriaLabel,
  page: pagination.pageAriaLabel,
  previousPage: pagination.previousPageAriaLabel,
  nextPage: pagination.nextPageAriaLabel,
});

export const wrapIfItsList = ({
  tag = '',
  items,
}: {
  tag: string;
  items: ReactNode[];
}) =>
  ['ul', 'ol'].includes(tag)
    ? // eslint-disable-next-line react/no-array-index-key
      items.map((item, index) => <li key={index}>{item}</li>)
    : items;

export const itemListingMapper = (
  itemListing: ItemListing | ItemListingCarousel,
  currentPageUid: string
): ItemListingProps | ItemListingCarouselProps => {
  const {
    items: [itemsData],
    pageBlock: {
      heading: { headline },
    },
    filterSettings: filters,
  } = itemListing;

  if (
    (itemsData.typeName.includes('product_cards_by_ref') ||
      itemsData.typeName.includes('product_cards_by_category')) &&
    !filters?.length
  ) {
    const cardProducts = itemsData.typeName.includes('product_cards_by_ref')
      ? itemsData.items
          .filter((item) => 'uid' in item && item.uid !== currentPageUid)
          .map(
            (item) =>
              'fields' in item &&
              'productData' in item.fields &&
              item.fields.productData
          )
      : itemsData.items
          .filter(
            (item) =>
              'relatedPageUid' in item && item.relatedPageUid !== currentPageUid
          )
          .map((item) => 'productData' in item && item.productData);

    gtmService.emitViewItemList(
      cardProducts.filter(Boolean) as unknown as SalsifyProductData[],
      headline ? { item_list_name: headline } : undefined
    );
  }

  const reactItems = mapItems(itemsData, currentPageUid);

  if ('carousel' in itemListing) {
    return itemListingCarouselMapper(
      itemListing as ItemListingCarousel,
      reactItems
    );
  }

  const {
    componentId,
    pageBlock,
    fluidGridUI: [fluidGrid],
    type,
    filterSettings,
    pagination,
    loadMoreButton,
    initialNumberDisplayedArticles,
    uid,
  } = itemListing;

  const listingItems = wrapIfItsList({
    tag: fluidGrid.ui.tag,
    items: reactItems,
  });

  return {
    pageBlock: pageBlockMapper(pageBlock),
    ...(componentId && { id: componentId }),
    items: listingItems,
    rawItems: itemsData,
    type,
    fluidGrid: fluidGridMapper(fluidGrid),
    filterSettings: filterMapper(filterSettings),
    currentPageUid,
    initialNumberArticles: initialNumberDisplayedArticles,
    ...(pagination && { pagination: paginationMapper(pagination) }),
    ...(loadMoreButton && { loadMoreButton: buttonMapper(loadMoreButton) }),
    uid,
  } as ItemListingProps;
};
